import { useState, useMemo, useEffect } from "react";
import { useSpring, useTransition, animated } from "@react-spring/web";
import { X, Search, Check, FileCode, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Organization } from "@/stores/authStore_v1";
import type { Org360MetadataType, Org360Node } from "./org360Types";
import type { Org360MetadataItem } from "@/hooks/org360/useOrg360MetadataItems";
import { NODE_COLORS, DEFAULT_NODE_COLOR } from "./org360Constants";

const OBJECT_TYPES = new Set(["CustomObject", "StandardObject"]);

interface OrgContextPanelProps {
  open: boolean;
  onClose: () => void;
  org: Organization;
  metadataTypes?: Org360MetadataType[];
  selectedType?: string;
  onTypeSelect: (type: string | undefined) => void;
  items?: Org360MetadataItem[];
  itemsLoading?: boolean;
  onNodeClick?: (node: Org360Node) => void;
  onFieldSecurity?: (objectName: string) => void;
}

// Each row owns its animated checkmark — avoids conditional rendering width jitter
function AnimatedCheck({ visible, color }: { visible: boolean; color: string }) {
  const spring = useSpring({
    opacity: visible ? 1 : 0,
    scale: visible ? 1 : 0.3,
    config: { tension: 480, friction: 20 },
  });
  return (
    <animated.span
      className="inline-flex flex-shrink-0"
      style={{ opacity: spring.opacity, transform: spring.scale.to((s) => `scale(${s})`) }}
    >
      <Check className="h-3 w-3" style={{ color }} />
    </animated.span>
  );
}

// ── Item row ──────────────────────────────────────────────────────────────────
function ItemRow({
  node,
  color,
  isObjectType,
  onClick,
  onFieldSecurity,
}: {
  node: Org360Node;
  color: string;
  isObjectType: boolean;
  onClick: () => void;
  onFieldSecurity?: (name: string) => void;
}) {
  const fileName = node.file_path
    ? node.file_path.split("/").pop()
    : node.full_name ?? null;

  return (
    <div className="flex items-center group hover:bg-muted/60 transition-colors duration-100">
      <button
        type="button"
        onClick={onClick}
        className="flex-1 flex items-start gap-2.5 px-3 py-2 text-left min-w-0"
      >
        <span
          className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5"
          style={{ background: color }}
        />
        <div className="flex-1 min-w-0">
          <span className="block text-[12px] font-mono truncate text-foreground
            group-hover:text-primary transition-colors duration-100">
            {node.label}
          </span>
          {fileName && (
            <span className="block text-[10px] font-mono truncate text-muted-foreground/70 mt-0.5">
              {fileName}
            </span>
          )}
        </div>
      </button>

      {/* Field Security shortcut — only for object types */}
      {isObjectType && onFieldSecurity && (
        <button
          type="button"
          onClick={() => onFieldSecurity(node.label)}
          title={`Field Security · ${node.label}`}
          className="flex-shrink-0 grid place-items-center w-7 h-7 mr-1.5 rounded
            text-muted-foreground/40 hover:text-primary hover:bg-primary/10
            opacity-0 group-hover:opacity-100 transition-all duration-150"
        >
          <ShieldCheck className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

export function OrgContextPanel({
  open,
  onClose,
  metadataTypes = [],
  selectedType,
  onTypeSelect,
  items = [],
  itemsLoading = false,
  onNodeClick,
  onFieldSecurity,
}: OrgContextPanelProps) {
  const isObjectType = selectedType ? OBJECT_TYPES.has(selectedType) : false;
  const [typeSearch, setTypeSearch] = useState("");
  const [itemSearch, setItemSearch] = useState("");

  // Reset item search when selected type changes
  useEffect(() => {
    setItemSearch("");
  }, [selectedType]);

  const panelSpring = useSpring({
    transform: open ? "translateX(0%)" : "translateX(-105%)",
    opacity: open ? 1 : 0,
    config: { tension: 300, friction: 28 },
  });

  const activeColor = selectedType
    ? (NODE_COLORS[selectedType] ?? DEFAULT_NODE_COLOR)
    : DEFAULT_NODE_COLOR;

  const chipSpring = useSpring({
    opacity: selectedType ? 1 : 0,
    x: selectedType ? 0 : -10,
    scale: selectedType ? 1 : 0.82,
    config: { tension: 400, friction: 22 },
  });

  // Type rail: compress when type is selected to make room for item table
  const typeRailSpring = useSpring({
    maxHeight: selectedType ? 180 : 9999,
    config: { tension: 320, friction: 28 },
  });

  // Item table: slide in from bottom when type selected
  const tableTransition = useTransition(selectedType ?? null, {
    from:  { opacity: 0, y: 16 },
    enter: { opacity: 1, y: 0 },
    leave: { opacity: 0, y: 8 },
    config: { tension: 340, friction: 28 },
    exitBeforeEnter: true,
  });

  const filteredTypes = useMemo(() => {
    if (!typeSearch.trim()) return metadataTypes;
    const q = typeSearch.toLowerCase();
    return metadataTypes.filter((mt) => mt.metadata_type.toLowerCase().includes(q));
  }, [metadataTypes, typeSearch]);

  // Items come from Neo4j (the graph's source of truth) as {id, full_name,
  // metadata_type, file_path}; adapt to the node shape the rows/click expect.
  const itemNodes = useMemo<Org360Node[]>(
    () =>
      items.map((it) => ({
        id: it.id,
        label: it.full_name,
        type: it.metadata_type,
        full_name: it.full_name,
        file_path: it.file_path,
      })),
    [items],
  );

  const filteredItems = useMemo(() => {
    if (!selectedType) return [];
    if (!itemSearch.trim()) return itemNodes;
    const q = itemSearch.toLowerCase();
    return itemNodes.filter(
      (n) =>
        n.label.toLowerCase().includes(q) ||
        (n.file_path ?? "").toLowerCase().includes(q) ||
        (n.full_name ?? "").toLowerCase().includes(q),
    );
  }, [itemNodes, selectedType, itemSearch]);

  const totalForType = itemNodes.length;

  return (
    <animated.aside
      className="absolute inset-y-0 left-0 z-30 w-72 max-w-[calc(100%-2rem)] flex flex-col
        bg-card/95 backdrop-blur-md border-r border-border overflow-hidden"
      style={panelSpring}
      aria-hidden={!open}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-4 pt-4 pb-3 flex-shrink-0 min-w-0">
        <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-muted-foreground whitespace-nowrap flex-shrink-0">
          Metadata Types
        </span>
        <animated.div
          className="flex items-center min-w-0 overflow-hidden"
          style={{
            opacity: chipSpring.opacity,
            transform: chipSpring.x.to(
              (x) => `translateX(${x}px) scale(${chipSpring.scale.get()})`,
            ),
            pointerEvents: selectedType ? "auto" : "none",
            maxWidth: selectedType ? "120px" : "0px",
          }}
        >
          <span
            className="inline-flex items-center gap-1 pl-1.5 pr-1 py-0.5 rounded-full
              text-[10px] font-mono font-semibold border w-full min-w-0"
            style={{
              color: activeColor,
              borderColor: `${activeColor}50`,
              background: `${activeColor}14`,
            }}
          >
            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: activeColor }} />
            <span className="truncate min-w-0">{selectedType}</span>
            <button
              type="button"
              onClick={() => onTypeSelect(undefined)}
              aria-label="Clear filter"
              className="flex-shrink-0 ml-0.5 opacity-70 hover:opacity-100 transition-opacity"
            >
              <X className="h-2.5 w-2.5" />
            </button>
          </span>
        </animated.div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close panel"
          className="ml-auto flex-shrink-0 grid place-items-center w-6 h-6 rounded-full
            text-muted-foreground hover:bg-muted hover:text-foreground transition-colors duration-150"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Type search */}
      <div className="px-3 pb-2 flex-shrink-0">
        <div className="flex items-center gap-2 px-2.5 h-8 rounded-lg border border-border bg-muted/50">
          <Search className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
          <input
            type="text"
            value={typeSearch}
            onChange={(e) => setTypeSearch(e.target.value)}
            placeholder="Search metadata types…"
            className="flex-1 bg-transparent text-[12px] font-mono text-foreground
              placeholder:text-muted-foreground outline-none min-w-0"
          />
          {typeSearch && (
            <button type="button" onClick={() => setTypeSearch("")}
              className="text-muted-foreground hover:text-foreground transition-colors">
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      {/* Type list — compresses when a type is selected */}
      <animated.div
        style={{ maxHeight: typeRailSpring.maxHeight, overflow: "hidden", flexShrink: 0 }}
      >
        <div className="overflow-y-auto px-2 pb-2 space-y-0.5" style={{ maxHeight: "inherit" }}>
          {!typeSearch && (
            <button
              type="button"
              onClick={() => onTypeSelect(undefined)}
              className={cn(
                "w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg",
                "text-[12px] font-mono transition-colors duration-150",
                !selectedType
                  ? "bg-muted text-foreground font-semibold"
                  : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
              )}
            >
              <span>All types</span>
              <span className="text-[10px] text-muted-foreground font-mono">
                {metadataTypes.reduce((s, m) => s + m.count, 0)}
              </span>
            </button>
          )}

          {filteredTypes.length === 0 && typeSearch && (
            <p className="px-2.5 py-4 text-[12px] text-muted-foreground font-mono text-center">
              No match for "{typeSearch}"
            </p>
          )}

          {filteredTypes.map((mt) => {
            const color = NODE_COLORS[mt.metadata_type] ?? DEFAULT_NODE_COLOR;
            const isActive = selectedType === mt.metadata_type;
            return (
              <button
                key={mt.metadata_type}
                type="button"
                onClick={() => onTypeSelect(isActive ? undefined : mt.metadata_type)}
                className={cn(
                  "w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg",
                  "text-[12px] font-mono transition-colors duration-150",
                  isActive
                    ? "text-foreground font-semibold"
                    : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
                )}
                style={isActive ? { background: `${color}14` } : undefined}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
                  <span className="truncate">{mt.metadata_type}</span>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <span className="text-[10px] text-muted-foreground font-mono">{mt.count}</span>
                  <AnimatedCheck visible={isActive} color={color} />
                </div>
              </button>
            );
          })}
        </div>
      </animated.div>

      {/* Item table — slides in when type is selected */}
      {tableTransition((style, type) =>
        type ? (
          <animated.div
            style={style}
            className="flex flex-col flex-1 min-h-0 border-t border-border"
          >
            {/* Table header */}
            <div className="px-3 pt-3 pb-2 flex-shrink-0">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <FileCode className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                    {type}
                  </span>
                </div>
                <span className="font-mono text-[10px] text-muted-foreground">
                  {filteredItems.length}
                  {filteredItems.length !== totalForType && ` / ${totalForType}`}
                </span>
              </div>

              {/* Item search */}
              <div className="flex items-center gap-2 px-2.5 h-7 rounded-lg border border-border bg-muted/50">
                <Search className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                <input
                  type="text"
                  value={itemSearch}
                  onChange={(e) => setItemSearch(e.target.value)}
                  placeholder={`Search ${type}…`}
                  className="flex-1 bg-transparent text-[11px] font-mono text-foreground
                    placeholder:text-muted-foreground outline-none min-w-0"
                />
                {itemSearch && (
                  <button type="button" onClick={() => setItemSearch("")}
                    className="text-muted-foreground hover:text-foreground transition-colors">
                    <X className="h-2.5 w-2.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Item list */}
            <div className="flex-1 overflow-y-auto min-h-0 pb-2">
              {itemsLoading ? (
                <p className="px-4 py-6 text-[12px] text-muted-foreground font-mono text-center">
                  Loading {type}…
                </p>
              ) : filteredItems.length === 0 ? (
                <p className="px-4 py-6 text-[12px] text-muted-foreground font-mono text-center">
                  {itemSearch ? `No match for "${itemSearch}"` : "No items found."}
                </p>
              ) : (
                filteredItems.map((node) => (
                  <ItemRow
                    key={node.id}
                    node={node}
                    color={NODE_COLORS[node.type] ?? DEFAULT_NODE_COLOR}
                    isObjectType={isObjectType}
                    onClick={() => onNodeClick?.(node)}
                    onFieldSecurity={onFieldSecurity}
                  />
                ))
              )}
            </div>
          </animated.div>
        ) : null
      )}
    </animated.aside>
  );
}
